/**
 * dateUtils.ts — Timezone-aware date/time formatting
 *
 * All timestamps on the platform should display in the company's local timezone.
 * The timezone is loaded from the public branding endpoint at app boot
 * and stored in window.__companyTimezone.
 *
 * Fallback: VITE_DEFAULT_TIMEZONE env var → 'America/Montreal'
 */

export const DEFAULT_TIMEZONE = (import.meta as any).env?.VITE_DEFAULT_TIMEZONE || 'America/Montreal'

export function getTimezone(): string {
  return (window as any).__companyTimezone || DEFAULT_TIMEZONE
}

export function setCompanyTimezone(tz: string): void {
  if (tz) {
    (window as any).__companyTimezone = tz
  }
}

export function formatDateTime(isoString: string | Date | null | undefined): string {
  if (!isoString) return '—'
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: getTimezone(),
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(isoString))
  } catch {
    return String(isoString)
  }
}

export function formatDate(isoString: string | Date | null | undefined): string {
  if (!isoString) return '—'
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: getTimezone(),
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(isoString))
  } catch {
    return String(isoString)
  }
}

export function formatTime(isoString: string | Date | null | undefined): string {
  if (!isoString) return '—'
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: getTimezone(),
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(isoString))
  } catch {
    return String(isoString)
  }
}

export function formatSmartTime(isoString: string | Date | null | undefined): string {
  if (!isoString) return '—'
  try {
    const d = new Date(isoString)
    const now = new Date()
    const tz = getTimezone()
    const dStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
    const nowStr = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now)
    if (dStr === nowStr) {
      return new Intl.DateTimeFormat('en-CA', { timeZone: tz, hour: '2-digit', minute: '2-digit' }).format(d)
    }
    return new Intl.DateTimeFormat('en-CA', { timeZone: tz, month: 'short', day: 'numeric' }).format(d)
  } catch {
    return String(isoString)
  }
}

export function formatDateWithOptions(
  isoString: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string {
  if (!isoString) return '—'
  try {
    const { timeZone, ...safeOptions } = options
    return new Intl.DateTimeFormat('en-CA', {
      ...safeOptions,
      timeZone: getTimezone(),
    }).format(new Date(isoString))
  } catch {
    return String(isoString)
  }
}

export function formatTimeWithOptions(
  isoString: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = {}
): string {
  if (!isoString) return '—'
  try {
    const { timeZone, ...safeOptions } = options
    return new Intl.DateTimeFormat('en-CA', {
      ...safeOptions,
      timeZone: getTimezone(),
    }).format(new Date(isoString))
  } catch {
    return String(isoString)
  }
}
